import type { Service } from '../graphqlEnums'
import type { CreatePostInput } from './graphqlTypes'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import { getComposerStore } from '../store/composerStore'
import { selectEditorForDraft } from '../store/editorSlice'
import type { ChannelVariant } from '../store/postSlice/types'
import { channelVariantToCreatePostInput } from './channelVariantToCreatePostInput'
import {
  checkCreatePostPayloadDrift,
  reportCreatePostPayloadBuildFailure,
} from './createPostPayloadDrift'
import { mapToCreatePostInput } from './mapToCreatePostInput'
import { resolveService } from './mapSharedPostFields'
import { serviceRegistry } from './metadata'
import type { MapperContext, ProfileRef } from './types'

/** Builds the Zustand-sourced payload and runs the drift check against it. */
function buildAndCheckVariantInput(
  draft: Draft,
  ctx: MapperContext,
  service: Service,
  variant: ChannelVariant,
  profileId: string,
): CreatePostInput {
  const metadata = serviceRegistry[service].mapper(draft, {
    ...ctx,
    channelVariant: variant,
  })
  const newInput = channelVariantToCreatePostInput(
    variant,
    service,
    metadata,
    ctx,
  )
  checkCreatePostPayloadDrift(draft, ctx, profileId, service, newInput)
  return newInput
}

/**
 * Fans the enabled per-service drafts out into one `CreatePostInput` per
 * matching selected profile. Skips the omni draft — only concrete
 * per-service drafts map to a channel.
 *
 * A profile with a mirrored `ChannelVariant` always builds the
 * Zustand-sourced payload and runs `checkCreatePostPayloadDrift` against
 * it — independent of `useZustandPayload` — so drift signal keeps
 * accumulating regardless of the `composer-zustand-create-post` rollout.
 * `useZustandPayload` only decides which payload ships; the legacy mapper
 * is still the fallback whenever no variant exists yet. When the flag is
 * off, the comparison build is a side quest — a bug in the still-unramped
 * mapper must not break a save nothing here depends on, so failures there
 * are reported rather than thrown.
 */
export function buildCreatePostInputs(
  drafts: Draft[],
  profiles: ProfileRef[],
  context: Omit<
    MapperContext,
    'channelId' | 'selectedBoardServiceId' | 'supportsLocationTagging'
  >,
  channelVariants: Record<string, ChannelVariant> | undefined,
  useZustandPayload: boolean,
  editorStateEnabled: boolean,
): CreatePostInput[] {
  return drafts
    .filter((draft) => draft.service.name !== 'omni')
    .flatMap((draft) => {
      const service = resolveService(draft)

      return profiles
        .filter((profile) => profile.serviceName === draft.service.name)
        .map((profile) => {
          const ctx: MapperContext = {
            ...context,
            channelId: profile.id,
            selectedBoardServiceId: profile.selectedBoardServiceId,
            supportsLocationTagging: profile.supportsLocationTagging,
            editor: editorStateEnabled
              ? selectEditorForDraft(getComposerStore().getState(), draft.id)
              : undefined,
          }

          const variant = channelVariants?.[profile.id]
          if (!variant) {
            return mapToCreatePostInput(draft, ctx)
          }

          if (useZustandPayload) {
            return buildAndCheckVariantInput(
              draft,
              ctx,
              service,
              variant,
              profile.id,
            )
          }

          try {
            buildAndCheckVariantInput(draft, ctx, service, variant, profile.id)
          } catch (error) {
            reportCreatePostPayloadBuildFailure(profile.id, service, error)
          }
          return mapToCreatePostInput(draft, ctx)
        })
    })
}
